import { MoveLeft, MoveRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import GlobalPagination from "utils/GlobalPagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/Select";
import { Button } from "../../ui/Button";
import { cn } from "utils/cn";

const PaginationComponent = ({
  PaginationClassName,
  currentPageNo,
  limit,
  // changeLimit = () => {},
  // changePage,
  onChangePagination = () => {},
  totalPage,
  total,
  btnBaseClasses,
}) => {
  const [currentPage, setCurrentPage] = useState(currentPageNo || 1);
  const [prevDisabled, setPrevDisabled] = useState(false);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [lowerLimit, setLowerLimit] = useState(1);
  const [upperLimit, setUpperLimit] = useState(3);
  const start = (currentPage - 1) * limit + 1;
  const end = Math.min(currentPage * limit, total);
  const paginationInfo = `${total === 0 ? total : start} - ${end} of ${total}`;

  const handleSetCurrentPage = (page) => {
    // Clamp to 1 and totalPage
    const newPage = Math.max(1, Math.min(page, totalPage));
    setCurrentPage(newPage);
    onChangePagination(limit, newPage);

    // Dynamic pagination window
    if (newPage === 1) {
      setLowerLimit(1);
      setUpperLimit(3);
    } else if (newPage === totalPage) {
      setLowerLimit(Math.max(totalPage - 2, 1));
      setUpperLimit(totalPage);
    } else {
      setLowerLimit(newPage - 1);
      setUpperLimit(newPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 1) {
      handleSetCurrentPage(currentPage - 1);
      if (currentPage - 1 < lowerLimit) {
        setUpperLimit(upperLimit - 1);
        setLowerLimit(lowerLimit - 1);
      }
    }
  };

  const nextPage = () => {
    if (currentPage < totalPage) {
      handleSetCurrentPage(currentPage + 1);
      if (currentPage + 1 >= upperLimit) {
        setUpperLimit(upperLimit + 1);
        setLowerLimit(lowerLimit + 1);
      }
    }
  };

  const handleSelect = (val) => {
    onChangePagination(val, 1);
    setCurrentPage(1);
    setLowerLimit(1);
    setUpperLimit(3);
  };

  const renderPageButtons = () => {
    const buttons = [];
    for (let i = lowerLimit; i <= Math.min(upperLimit, totalPage); i++) {
      buttons.push(
        <Button
          key={i}
          size="sm"
          mode="icon"
          variant="ghost"
          className={cn(btnBaseClasses, "text-muted-foreground", {
            "bg-accent text-accent-foreground": currentPage === i,
          })}
          onClick={() => handleSetCurrentPage(i)}
        >
          {i}
        </Button>,
      );
    }
    return buttons;
  };
  useEffect(() => {
    if (currentPageNo && currentPageNo !== currentPage) {
      setCurrentPage(currentPageNo);

      if (currentPageNo === 1) {
        setLowerLimit(1);
        setUpperLimit(3);
      }
    }
  }, [currentPageNo]);
  const renderEllipsisPrevButton = () => {
    if (lowerLimit > 1) {
      return (
        <Button
          size="sm"
          mode="icon"
          className={btnBaseClasses}
          variant="ghost"
          onClick={() => {
            const newUpper = Math.max(upperLimit - 3, 1);
            const newLower = newUpper - 2;
            setLowerLimit(newLower);
            setUpperLimit(newUpper);
            handleSetCurrentPage(newUpper);
          }}
        >
          ...
        </Button>
      );
    }
    return null;
  };

  const renderEllipsisNextButton = () => {
    if (upperLimit < totalPage) {
      return (
        <Button
          className={btnBaseClasses}
          variant="ghost"
          size="sm"
          mode="icon"
          onClick={() => {
            const newLower = lowerLimit + 3;
            const newUpper = Math.min(newLower + 2, totalPage);
            setLowerLimit(newLower);
            setUpperLimit(newUpper);
            handleSetCurrentPage(newLower);
          }}
        >
          ...
        </Button>
      );
    }
    return null;
  };

  useEffect(() => {
    if (totalPage <= 1) {
      setPrevDisabled(true);
    } else if (currentPage === 1) {
      setPrevDisabled(true);
    } else {
      setPrevDisabled(false);
    }
  }, [currentPage, totalPage]);

  useEffect(() => {
    if (totalPage <= 1) {
      setNextDisabled(true);
    } else if (currentPage === totalPage) {
      setNextDisabled(true);
    } else {
      setNextDisabled(false);
    }
  }, [currentPage, totalPage]);

  return (
    <div
      className={cn(
        PaginationClassName,
        "flex flex-wrap flex-col sm:flex-row justify-between items-center gap-2.5 py-2.5 sm:py-0 grow",
      )}
    >
      <div className="flex flex-wrap items-center space-x-2.5 pb-2.5 sm:pb-0 order-2 sm:order-1">
        <div className="text-sm text-black text-muted-foreground">Show</div>
        <Select
          value={`${limit}`}
          indicatorPosition="right"
          onValueChange={(value) => {
            handleSelect(Number(value));
          }}
        >
          <SelectTrigger className="w-fit h-8" size="sm">
            <SelectValue placeholder={`${limit}`} />
          </SelectTrigger>
          <SelectContent side="top" className="min-w-[50px]">
            {GlobalPagination.limitOptions?.map((size) => (
              <SelectItem key={size} value={`${size}`}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-sm text-black text-muted-foreground">per page</div>
      </div>
      <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center gap-2.5 pt-2.5 sm:pt-0 order-1 sm:order-2">
        <div className="text-sm text-black text-muted-foreground text-nowrap order-2 sm:order-1">
          {paginationInfo}
        </div>
        {totalPage > 1 && (
          <div className="flex items-center space-x-1 order-1 sm:order-2">
            <Button
              size="sm"
              mode="icon"
              variant="ghost"
              // className={btnArrowClasses}
              onClick={() => previousPage()}
              disabled={prevDisabled}
            >
              <span className="sr-only">Go to previous page</span>
              <MoveLeft className="size-4" />
            </Button>

            {renderEllipsisPrevButton()}

            {renderPageButtons()}

            {renderEllipsisNextButton()}

            <Button
              size="sm"
              mode="icon"
              variant="ghost"
              // className={btnArrowClasses}
              onClick={nextPage}
              disabled={nextDisabled}
            >
              <span className="sr-only">Go to next page</span>
              <MoveRight className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaginationComponent;
